import { useRef, useState } from 'react';
import { Modal } from '../components/Modal';
import { api, ApiError, type GameId, type SpecialProgress } from '../lib/api';
import type { ServerTask } from '../state';

export type TaskResult =
    | { kind: 'paid'; amount: number }
    | { kind: 'pending' }
    | { kind: 'holding'; holdUntil?: string }
    | { kind: 'appeal'; round: 1 | 2 };

type Props = {
    task: ServerTask;
    onClose: () => void;
    onComplete: (task: ServerTask, result: TaskResult) => void;
};

const GAME_LABELS: Record<GameId, string> = {
    boxes: 'BSP BOXES', fast: 'FAST GIVEAWAY', g667: '667', battle: 'BOG BATTLE', piggy: 'PIGGY BANK',
};

/** Shown before every re-review request (Основной холст: "сделать
 *  предупреждение у пересмотра и арбитража"). */
export const APPEAL_WARNING =
    'Пересмотр — для настоящих ошибок проверки. Если запрашивать его по пустякам, '
    + 'администрация может лишить вас права на пересмотр.';

/**
 * Execution flow for one task, by kind:
 *   special     — progress towards the goal, "Начать" / "Забрать"
 *   channel_sub — subscribe, then our bot checks it ("Проверить")
 *   custom      — "Начать" reveals the instructions, then a screenshot or the
 *                 check word; a rejection (or a wrong word) leads to the
 *                 re-review form
 *   post_view   — legacy open-and-confirm
 */
export function TaskExecModal({ task, onClose, onComplete }: Props) {
    return (
        <Modal title={task.title} onClose={onClose}>
            <p className="popup-text">
                {task.special?.type === 'deposit_ladder'
                    ? <>Бонус: <b className="text-accent">+{String(task.special.params['stepPct'] ?? 5)}% к следующему депозиту</b></>
                    : <>Награда: <b className="text-accent">+{task.reward} BSP</b></>}
                {task.holdHours > 0 && <> · Холд: <b>{task.holdHours} ч</b></>}
                {task.isPremiumOnly && <> · <span style={{ color: '#a78bfa' }}>Premium</span></>}
            </p>
            {task.description && <p className="popup-text">{task.description}</p>}

            {task.kind === 'special' && <SpecialFlow task={task} onComplete={onComplete} />}
            {task.kind === 'channel_sub' && <LinkFlow task={task} onComplete={onComplete} subscription />}
            {task.kind === 'post_view' && <LinkFlow task={task} onComplete={onComplete} />}
            {task.kind === 'custom' && <RegularFlow task={task} onComplete={onComplete} />}
        </Modal>
    );
}

// ─── Special ──────────────────────────────────────────────────────────────────

function SpecialFlow({ task, onComplete }: { task: ServerTask; onComplete: Props['onComplete'] }) {
    const sp = task.special!;
    const [progress, setProgress] = useState<SpecialProgress>(sp.progress);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const what =
        sp.type === 'play_game' ? `Сыграйте в ${GAME_LABELS[sp.params['game'] as GameId] ?? 'игру'}: ${String(sp.params['count'] ?? 1)} раз.` :
        sp.type === 'play_each_game' ? `Сыграйте хотя бы раз в каждую игру: ${Object.values(GAME_LABELS).join(', ')}.` :
        sp.type === 'deposit_amount' ? `Пополните баланс в сумме на ${Number(sp.params['amount'] ?? 0).toLocaleString('ru-RU')} BSP.` :
        sp.type === 'daily_bonus' ? 'Бонус можно забирать раз в день (сброс в 00:00 МСК).' :
        `Каждый депозит сегодня увеличивает бонус к следующему на +${String(sp.params['stepPct'] ?? 5)}%, `
        + `до +${String(sp.params['maxPct'] ?? 15)}%. Бонус начисляется автоматически. Сброс в 00:00 МСК.`;

    const start = async () => {
        setBusy(true); setErr(null);
        try {
            await api.startTask(task.id);
            setProgress((p) => ({ ...p, started: true }));
        } catch (e) { setErr(describe(e)); } finally { setBusy(false); }
    };

    const claim = async () => {
        setBusy(true); setErr(null);
        try {
            const r = await api.completeTask(task.id);
            onComplete(task, { kind: 'paid', amount: r.reward ?? task.reward });
        } catch (e) {
            if (e instanceof ApiError && e.code === 'not_done') {
                const p = (e.body as { progress?: SpecialProgress } | undefined)?.progress;
                if (p) setProgress(p);
                setErr('Условие ещё не выполнено.');
            } else setErr(describe(e));
        } finally { setBusy(false); }
    };

    return (
        <>
            <p className="popup-text">{what}</p>
            {progress.started && sp.type !== 'deposit_ladder' && sp.type !== 'daily_bonus' && (
                <div className="info-card" data-testid="special-progress">{progress.label}</div>
            )}
            {sp.type === 'deposit_ladder' && (
                <div className="info-card" data-testid="special-progress">{progress.label}</div>
            )}
            {(sp.type === 'play_game' || sp.type === 'play_each_game' || sp.type === 'deposit_amount') && !progress.started && (
                <p className="popup-text text-muted f-12">Засчитывается всё, что вы сделаете после нажатия «Начать».</p>
            )}

            {sp.type !== 'deposit_ladder' && (
                progress.claimed ? (
                    <div className="info-card text-center" style={{ borderColor: 'var(--green)' }}>✓ Награда получена</div>
                ) : !progress.started ? (
                    <button type="button" className="action-btn btn-blue" disabled={busy} onClick={start}>
                        {busy ? '…' : 'Начать'}
                    </button>
                ) : (
                    <button type="button" className="action-btn btn-gold" disabled={busy} onClick={claim}>
                        {busy ? '…' : progress.done ? (sp.type === 'daily_bonus' ? 'Забрать бонус' : 'Забрать награду') : 'Проверить выполнение'}
                    </button>
                )
            )}
            {err && <p className="popup-text" style={{ color: 'var(--red)', marginTop: 8 }}>{err}</p>}
        </>
    );
}

// ─── Link tasks (subscription / view) ─────────────────────────────────────────

function LinkFlow({ task, onComplete, subscription = false }: {
    task: ServerTask; onComplete: Props['onComplete']; subscription?: boolean;
}) {
    const [started, setStarted] = useState(task.myStatus === 'in_progress');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const url = task.url ?? 'https://t.me/BogStarsBot';

    const start = async () => {
        setBusy(true); setErr(null);
        try { await api.startTask(task.id); setStarted(true); }
        catch (e) { setErr(describe(e)); } finally { setBusy(false); }
    };
    const check = async () => {
        setBusy(true); setErr(null);
        try {
            const r = await api.completeTask(task.id);
            if (r.status === 'holding') onComplete(task, { kind: 'holding', holdUntil: r.holdUntil });
            else onComplete(task, { kind: 'paid', amount: r.reward ?? task.reward });
        } catch (e) { setErr(describe(e)); } finally { setBusy(false); }
    };

    return (
        <>
            {subscription && (
                <p className="popup-text text-muted f-12">
                    Подпишитесь на канал и нажмите «Проверить» — бот сам проверит подписку.
                </p>
            )}
            {subscription && task.holdHours > 0 && (
                <div className="withdraw-warn">
                    После проверки идёт холд {task.holdHours} ч: не отписывайтесь, иначе задание не засчитается.
                </div>
            )}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                <a href={url} target="_blank" rel="noreferrer noopener"
                    className="action-btn btn-outline-gold"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', flex: 1 }}>
                    {subscription ? 'Подписаться' : 'Посмотреть'}
                </a>
                {!started ? (
                    <button type="button" className="action-btn btn-blue" onClick={start} disabled={busy} style={{ flex: 1 }}>
                        {busy ? '…' : 'Начать'}
                    </button>
                ) : (
                    <button type="button" className="action-btn btn-green" onClick={check} disabled={busy} style={{ flex: 1 }}>
                        {busy ? '…' : 'Проверить'}
                    </button>
                )}
            </div>
            {err && <p className="popup-text" style={{ color: 'var(--red)', marginTop: 8 }}>{err}</p>}
        </>
    );
}

// ─── Regular tasks ────────────────────────────────────────────────────────────

type RegularPhase = 'idle' | 'started' | 'wrong_word' | 'appeal' | 'appeal_sent' | 'appeal_pending';

function RegularFlow({ task, onComplete }: { task: ServerTask; onComplete: Props['onComplete'] }) {
    const sub = task.mySubmission;
    const isWord = task.checkType === 'word';
    const initial: RegularPhase =
        sub?.appeals.some((a) => a.status === 'pending') ? 'appeal_pending'
        : sub && sub.status === 'rejected' && sub.canAppeal ? 'appeal'
        : 'idle';

    const [phase, setPhase]   = useState<RegularPhase>(initial);
    const [instructions, setInstructions] = useState<string | null>(null);
    const [link, setLink]     = useState<string | null>(task.url);
    const [answer, setAnswer] = useState('');
    const [comment, setComment] = useState('');
    const [shot, setShot]     = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [subId, setSubId]   = useState<number | null>(sub?.id ?? null);
    const [busy, setBusy]     = useState(false);
    const [err, setErr]       = useState<string | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const round = sub?.nextAppealRound ?? 1;
    const reason = sub?.status === 'rejected'
        ? (sub.appeals[sub.appeals.length - 1]?.decisionComment ?? sub.moderatorComment)
        : phase === 'wrong_word' || phase === 'appeal' ? 'Неверное проверочное слово' : null;

    const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] ?? null;
        if (f && f.size > 8 * 1024 * 1024) {
            setErr('Файл слишком большой (макс. 8 МБ).');
            setShot(null); setPreview(null);
            if (fileRef.current) fileRef.current.value = '';
            return;
        }
        setErr(null); setShot(f);
        if (f) {
            const r = new FileReader();
            r.onload = (ev) => setPreview(ev.target?.result as string);
            r.readAsDataURL(f);
        } else setPreview(null);
    };

    const start = async () => {
        setBusy(true); setErr(null);
        try {
            const r = await api.startTask(task.id);
            setInstructions(r.instructions ?? null);
            if (r.url) setLink(r.url);
            setPhase('started');
        } catch (e) { setErr(describe(e)); } finally { setBusy(false); }
    };

    const submit = async () => {
        setBusy(true); setErr(null);
        try {
            const r = await api.submitTask(task.id, {
                responseText: isWord ? answer.trim() : (comment.trim() || undefined),
                screenshotBase64: shot ? await fileToBase64(shot) : undefined,
            });
            if (r.status === 'accepted') onComplete(task, { kind: 'paid', amount: task.reward });
            else if (r.status === 'pending') onComplete(task, { kind: 'pending' });
            else { setSubId(r.id); setShot(null); setPreview(null); setPhase('wrong_word'); }
        } catch (e) { setErr(describe(e)); } finally { setBusy(false); }
    };

    const appeal = async () => {
        if (subId === null) return;
        setBusy(true); setErr(null);
        try {
            const r = await api.appealSubmission(subId, {
                comment: comment.trim(),
                screenshotBase64: shot ? await fileToBase64(shot) : undefined,
            });
            setPhase('appeal_sent');
            onComplete(task, { kind: 'appeal', round: r.round });
        } catch (e) { setErr(describe(e)); } finally { setBusy(false); }
    };

    const needShot = task.requireScreenshot;
    const canSubmit = isWord ? answer.trim().length > 0 : (!needShot || shot !== null);
    const canAppeal = comment.trim().length > 0 && (!needShot || shot !== null);

    const filePicker = (label: string) => (
        <div className="input-group">
            <label>{label}</label>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
                style={{ display: 'none' }} onChange={onFile} data-testid="screenshot-input" />
            <button type="button" className="action-btn btn-outline-gold" onClick={() => fileRef.current?.click()}>
                {shot ? `📎 ${shot.name}` : '📎 Выбрать скриншот'}
            </button>
            {preview && (
                <img src={preview} alt="Скриншот"
                    style={{ marginTop: 8, maxWidth: '100%', maxHeight: 200, borderRadius: 8, objectFit: 'contain' }} />
            )}
        </div>
    );

    return (
        <>
            {phase === 'idle' && (
                <>
                    <p className="popup-text text-muted f-12">
                        {isWord
                            ? 'После «Начать» откроются инструкции: выполните их и введите проверочное слово.'
                            : 'После «Начать» откроются инструкции: выполните их и пришлите скриншот на проверку.'}
                    </p>
                    <button type="button" className="action-btn btn-blue" disabled={busy} onClick={start}>
                        {busy ? '…' : 'Начать'}
                    </button>
                </>
            )}

            {phase === 'started' && (
                <>
                    {instructions && (
                        <div className="task-instructions" data-testid="task-instructions">
                            <b>Инструкция:</b>{'\n'}{instructions}
                        </div>
                    )}
                    {link && (
                        <a href={link} target="_blank" rel="noreferrer noopener" className="action-btn btn-outline-gold"
                            style={{ display: 'flex', justifyContent: 'center', textDecoration: 'none', marginTop: 8 }}>
                            Перейти по ссылке
                        </a>
                    )}
                    {isWord ? (
                        <div className="input-group" style={{ marginTop: 8 }}>
                            <label>Проверочное слово</label>
                            <input value={answer} maxLength={50} onChange={(e) => setAnswer(e.target.value)}
                                placeholder="Одно слово" autoComplete="off" />
                            <span className="hint">Регистр букв не важен. Слово можно ввести только один раз.</span>
                        </div>
                    ) : (
                        <>
                            {filePicker('Скриншот (обязательно)')}
                            <div className="input-group">
                                <label>Комментарий (необязательно)</label>
                                <textarea rows={2} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
                            </div>
                        </>
                    )}
                    <button type="button" className="action-btn btn-green" disabled={!canSubmit || busy} onClick={submit}
                        style={{ marginTop: 8 }}>
                        {busy ? 'Отправляем…' : isWord ? 'Проверить слово' : 'Отправить на проверку'}
                    </button>
                </>
            )}

            {phase === 'wrong_word' && (
                <>
                    <div className="withdraw-warn" style={{ borderColor: 'var(--red)', color: '#ff8585' }}>
                        Слово неверное — задание не засчитано.
                    </div>
                    <p className="popup-text">
                        Если вы уверены, что всё сделали правильно, запросите ручную проверку:
                        сначала её рассмотрит заказчик задания, затем — администрация.
                    </p>
                    <button type="button" className="action-btn btn-outline-gold" onClick={() => setPhase('appeal')}>
                        Запросить ручную проверку
                    </button>
                </>
            )}

            {phase === 'appeal' && (
                <>
                    {reason && <div className="task-card-note">Причина отказа: {reason}</div>}
                    <p className="popup-text">
                        {round === 2
                            ? 'Повторный пересмотр рассмотрит администрация. Это последняя возможность.'
                            : 'Пересмотр рассмотрит заказчик задания. Если он откажет — можно будет обратиться к администрации.'}
                    </p>
                    <div className="appeal-warning" data-testid="appeal-warning">⚠️ {APPEAL_WARNING}</div>
                    <div className="input-group">
                        <label>Комментарий (обязательно)</label>
                        <textarea rows={3} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)}
                            placeholder="Почему заявку нужно пересмотреть" />
                    </div>
                    {filePicker(needShot ? 'Новый скриншот (обязательно)' : 'Скриншот (необязательно)')}
                    <button type="button" className="action-btn btn-gold" disabled={!canAppeal || busy} onClick={appeal}
                        style={{ marginTop: 8 }}>
                        {busy ? 'Отправляем…' : isWord && round === 1 && !sub ? 'Отправить на ручную проверку' : 'Запросить пересмотр'}
                    </button>
                </>
            )}

            {(phase === 'appeal_sent' || phase === 'appeal_pending') && (
                <div className="info-card text-center" style={{ borderColor: 'var(--accent)' }}>
                    <p className="popup-text fw-900">Запрос на пересмотр отправлен.</p>
                    <p className="popup-text text-muted f-12">Вы получите уведомление, когда по нему примут решение.</p>
                </div>
            )}

            {err && <p className="popup-text" style={{ color: 'var(--red)', marginTop: 8 }}>{err}</p>}
        </>
    );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ERRORS: Record<string, string> = {
    not_subscribed:      'Бот не видит подписку. Подпишитесь и нажмите «Проверить» ещё раз.',
    check_unavailable:   'Не получилось проверить подписку. Попробуйте через минуту.',
    premium_required:    'Задание доступно только для Telegram Premium.',
    already_submitted:   'Вы уже сдавали это задание.',
    already_attempted:   'Вы уже начали это задание.',
    screenshot_required: 'Нужен скриншот.',
    answer_required:     'Введите проверочное слово.',
    invalid_image:       'Файл повреждён или не является изображением.',
    no_slots:            'Свободные места закончились.',
    unavailable:         'Задание сейчас недоступно.',
    user_banned:         'Ваш аккаунт заблокирован.',
    not_started:         'Сначала нажмите «Начать».',
    already_claimed:     'Награда уже получена.',
    appeals_banned:      'Вы лишены права на пересмотр.',
    appeal_pending:      'Запрос на пересмотр уже рассматривается.',
    appeals_exhausted:   'Возможности пересмотра исчерпаны.',
    comment_required:    'Напишите комментарий.',
    task_closed:         'Задание закрыто — пересмотр недоступен.',
    too_many_requests:   'Слишком часто. Подождите пару секунд.',
};

function describe(e: unknown): string {
    if (e instanceof ApiError) return ERRORS[e.code] ?? `Ошибка: ${e.code}`;
    const msg = e instanceof Error ? e.message : String(e);
    if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) return 'Нет соединения с сервером.';
    return `Не получилось (${msg.slice(0, 80)}).`;
}

function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
        reader.onerror = () => reject(new Error('file_read_error'));
        reader.readAsDataURL(file);
    });
}
